import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatNewsTime } from './community/utils';
import './News.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://community.ilouli.com';

// 간단한 아이콘 컴포넌트
const Icon = ({ name, size = 16, className = '' }) => {
  const icons = {
    bell: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
    warning: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
    news: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
        <path d="M18 14h-8"/>
        <path d="M15 18h-5"/>
        <path d="M10 6h8v4h-8V6Z"/>
      </svg>
    ),
    bookmark: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    bookmarkFilled: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    externalLink: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
    ),
  };
  return <span className={className}>{icons[name] || null}</span>;
};

const News = () => {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryInsight, setCategoryInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);

  // URL search params -> state
  const selectedCategory = searchParams.get('category') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const searchQuery = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(searchQuery);

  // Helper to update search params (preserves other params)
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' || value === '1' && key === 'page') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Keep searchInput in sync when URL changes externally (e.g. browser back)
  useEffect(() => {
    const urlQ = searchParams.get('q') || '';
    setSearchInput(urlQ);
  }, [searchParams]);

  // 카테고리 로드
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/news/categories`);
        const data = await response.json();
        if (data.success) {
          // 전체 카테고리 사용
          setCategories(data.categories);
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // 뉴스 로드
  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }
      if (searchQuery && searchQuery.trim().length >= 2) {
        params.append('search', searchQuery.trim());
      }

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/news?${params}`, {
        headers
      });

      const data = await response.json();
      if (data.success) {
        setNews(data.articles);
        setTotalPages(data.totalPages);
      } else {
        setError(data.error || '뉴스를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('뉴스를 불러오는데 실패했습니다.');
      console.error('Failed to load news:', err);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory, searchQuery, getToken]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  // 검색 실행
  const handleSearch = () => {
    updateParams({ q: searchInput.trim(), page: '1' });
  };

  // 검색 초기화
  const handleClearSearch = () => {
    setSearchInput('');
    updateParams({ q: '', page: '1' });
  };

  // 카테고리별 AI 인사이트 로드
  useEffect(() => {
    const fetchCategoryInsight = async () => {
      if (!selectedCategory) {
        setCategoryInsight(null);
        return;
      }

      setInsightLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/news/summary/${selectedCategory}`);
        const data = await response.json();
        if (data.success) {
          setCategoryInsight(data);
        } else {
          setCategoryInsight(null);
        }
      } catch (err) {
        console.error('Failed to load category insight:', err);
        setCategoryInsight(null);
      } finally {
        setInsightLoading(false);
      }
    };

    fetchCategoryInsight();
  }, [selectedCategory]);

  // 북마크 토글
  const handleBookmark = async (articleId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/${articleId}/bookmark`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setNews(news.map(article =>
          article.id === articleId
            ? { ...article, is_bookmarked: data.bookmarked ? 1 : 0 }
            : article
        ));
      }
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
    }
  };

  // 카테고리 라벨
  const getCategoryLabel = useMemo(() => ({
    headlines: '헤드라인',
    business: '비즈니스',
    technology: '기술',
    entertainment: '엔터테인먼트',
    sports: '스포츠',
    world: '세계',
    ai: 'AI/인공지능',
    startup: '스타트업',
    security: '보안',
    economy: '경제',
    stock: '증권/주식',
    realestate: '부동산',
    industry: '산업',
    childcare: '육아',
    education: '교육',
    life: '라이프',
    healthcare: '의료',
    finance: '금융',
    semiconductor: '반도체',
    battery: '배터리',
    auto: '자동차',
    shipbuilding: '조선',
    bio: '바이오',
    defense: '국방',
    politics: '정치',
    diplomacy: '외교',
    accident: '사건/사고',
    environment: '환경',
    kpop: 'K-POP',
    drama: '드라마',
    game: '게임',
    travel: '여행'
  }), []);

  // formatDate: community/utils.js에서 import
  const formatDate = formatNewsTime;

  return (
    <div className="news-page">
      <div className="news-header">
        <div className="header-content">
          <h1>뉴스</h1>
          <p className="subtitle">카테고리별 최신 뉴스를 확인하세요</p>
        </div>
      </div>

      {/* 검색 섹션 */}
      <div className="news-search-section">
        <h3>뉴스 검색</h3>
        <div className="search-input-row">
          <input
            type="text"
            placeholder="검색어 입력... (제목, 내용, 키워드)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>검색</button>
        </div>
        {searchQuery && (
          <div className="search-result-info">
            <span className="search-query-tag">
              "{searchQuery}"
              <button onClick={handleClearSearch}>×</button>
            </span>
            <span className="search-result-text">검색 결과</span>
          </div>
        )}
      </div>

      {/* 카테고리 필터 */}
      <div className="news-filters">
        <div className="category-tabs">
          <button
            className={`category-tab ${selectedCategory === '' ? 'active' : ''}`}
            onClick={() => updateParams({ category: '', page: '1' })}
          >
            전체
          </button>
          {categories.map(cat => (
            <button
              key={cat.value}
              className={`category-tab ${selectedCategory === cat.value ? 'active' : ''}`}
              onClick={() => updateParams({ category: cat.value, page: '1' })}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* AI 인사이트 */}
      {selectedCategory && (
        <div className="ai-insight-section">
          {insightLoading ? (
            <div className="insight-loading">
              <div className="spinner-small"></div>
              <span>AI 분석 중...</span>
            </div>
          ) : categoryInsight ? (
            <>
              <div className="insight-header">
                <span className="insight-icon">🤖</span>
                <h3>AI 인사이트 - {categoryInsight.categoryLabel}</h3>
                <span className="insight-meta">{categoryInsight.totalArticles}개 기사 분석</span>
              </div>
              {categoryInsight.summary && (
                <div className="insight-summary">
                  <p>{categoryInsight.summary}</p>
                </div>
              )}
              {categoryInsight.themes && categoryInsight.themes.length > 0 && (
                <div className="insight-themes">
                  {categoryInsight.themes.slice(0, 3).map((theme, idx) => (
                    <div key={idx} className="theme-item">
                      <span className="theme-title">{theme.keyword}</span>
                      <span className="theme-summary">{theme.summary}</span>
                    </div>
                  ))}
                </div>
              )}
              {categoryInsight.hotKeywords && categoryInsight.hotKeywords.length > 0 && (
                <div className="insight-keywords">
                  <span className="keywords-label">🔥 핫 키워드</span>
                  <div className="keywords-list">
                    {categoryInsight.hotKeywords.slice(0, 8).map((kw, idx) => (
                      <span key={idx} className="hot-keyword">{kw.word}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="insight-empty">
              <span>해당 카테고리의 AI 분석 데이터가 없습니다.</span>
            </div>
          )}
        </div>
      )}

      {/* 뉴스 목록 */}
      <div className="news-content">
        {loading ? (
          <div className="news-loading">
            <div className="spinner"></div>
            <p>뉴스를 불러오는 중...</p>
          </div>
        ) : error ? (
          <div className="news-error">
            <Icon name="warning" size={48} className="error-icon" />
            <p>{error}</p>
            <button onClick={fetchNews}>다시 시도</button>
          </div>
        ) : news.length === 0 ? (
          <div className="news-empty">
            <Icon name="news" size={48} className="empty-icon" />
            <p>뉴스가 없습니다.</p>
          </div>
        ) : (
          <div className="news-list">
            {news.map(article => (
              <article key={article.id} className="news-item">
                <div className="news-item-main">
                  <div className="news-item-header">
                    <span className="news-category">
                      {getCategoryLabel[article.category] || article.category}
                    </span>
                    <span className="news-source">{article.source}</span>
                    <span className="news-time">{formatDate(article.pub_date)}</span>
                  </div>
                  <h3 className="news-title">
                    <a href={article.link} target="_blank" rel="noopener noreferrer">
                      {article.title}
                    </a>
                  </h3>
                  {article.description && (
                    <p className="news-description">
                      {article.description.length > 150
                        ? article.description.substring(0, 150) + '...'
                        : article.description}
                    </p>
                  )}
                </div>
                <div className="news-item-actions">
                  <button
                    className={`bookmark-btn ${article.is_bookmarked ? 'bookmarked' : ''}`}
                    onClick={() => handleBookmark(article.id)}
                    title={article.is_bookmarked ? '북마크 해제' : '북마크'}
                  >
                    <Icon name={article.is_bookmarked ? 'bookmarkFilled' : 'bookmark'} size={18} />
                  </button>
                  <a
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                    title="원문 보기"
                  >
                    <Icon name="externalLink" size={16} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="news-pagination">
            <button
              disabled={page === 1}
              onClick={() => updateParams({ page: String(page - 1) })}
            >
              이전
            </button>
            <span className="page-info">{page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => updateParams({ page: String(page + 1) })}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
